// =============================================================================
// SIREPPAR - Wizard de Documentación de RECONOCIMIENTO (constitución de partido)
// =============================================================================
// El apoderado de un partido en formación carga, en un solo asistente y solo en
// la fase DOCUMENTACION_PENDIENTE:
//   1. Documentos obligatorios del Art. 18/19.
//   2. Órganos declarados en el acta + sus autoridades promotoras (con cargo
//      opcional; "PROMOTORA" = sin cargo definido → cargo null + esPromotor).
//   3. Apoderados promotores (tipo APODERADO).
//
// Todas las personas cargadas acá son PROMOTORAS/promotores por definición (el
// backend marca esPromotor=true). Cada DNI se valida contra el padrón y se
// reutiliza si ya hay un documento verificado; si no, se sube frente/dorso.
// Mismo look & feel que el wizard de reempadronamiento (verde/teal, stepper).
// =============================================================================

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api, reconocimientoService, documentoService } from '../../services/api';
import { useReconocimientoApoderado } from '../../hooks/useReconocimientoApoderado';
import { TIPOS_SOLO_EXCEL, acceptParaTipo, esArchivoExcelOds, msgSoloExcel } from '../../utils/tiposDocumento';
import {
  TIPO_ORGANO_CERTIFICADORES,
  CARGO_CERTIFICADOR_FIRMAS,
  LABEL_CERTIFICADORES,
  LABEL_CARGO_CERTIFICADOR,
  MAX_CERTIFICADORES,
  esSeccionCertificadores,
  vocabularioOrgano,
} from '../../utils/certificadoresFirmas';

// Una fila de la lista de documentación del wizard.
//   plantilla → modelo descargable. Sale del backend (docs/), que es la MISMA fuente
//     que usan los adjuntos del correo: así la web y el mail entregan el mismo archivo.
//   sinCarga  → la pieza NO se sube al sistema; se entrega en papel o por correo. En
//     lugar del botón "Adjuntar" se muestra la vía de entrega, y la fila no cuenta
//     para el gate de "documentación completa".
interface FilaDoc {
  tipo: string;
  label: string;
  ref: string;
  ayuda?: string;
  plantilla?: { url: string; filename: string };
  sinCarga?: boolean;
}

// Nombres canónicos (los mismos en back y front, ver
// config/documentosObligatorios.ts → DOCS_OBLIGATORIOS_RECONOCIMIENTO).
const DOCS_OBLIGATORIOS: FilaDoc[] = [
  { tipo: 'ACTA_CONSTITUTIVA', label: 'Acta Constitutiva', ref: 'Art. 18 a' },
  { tipo: 'CARTA_ORGANICA', label: 'Carta Orgánica', ref: 'Art. 18 c' },
  { tipo: 'BASES_ACCION_POLITICA', label: 'Bases de Acción Política', ref: 'Art. 18 c' },
  { tipo: 'ACTA_DESIGNACION_AUTORIDADES_PROMOTORAS', label: 'Acta de Designación de Autoridades Promotoras', ref: 'Art. 18 d' },
  // El Listado (Excel) va PRIMERO y los avales después, porque es el orden del trabajo
  // real: se completa el Excel, se imprime y se hacen firmar las planillas.
  {
    tipo: 'LISTADO_ADHERENTES_EXCEL',
    label: 'Listado de Adherentes',
    ref: 'Art. 19',
    ayuda: 'Excel — usá el formato provisto',
    plantilla: { url: '/reconocimiento/listado-adherentes', filename: 'Listado de Adherentes.xlsx' },
  },
  // Los AVALES no se suben: son las planillas firmadas de puño, una por hoja de
  // adherentes, y se presentan en papel. Antes esta fila pedía un PDF y no ofrecía
  // plantilla; ahora es al revés: ofrece el modelo para imprimir y no pide carga.
  {
    tipo: 'PLANILLA_ADHERENTES_FIRMAS',
    label: 'Todas Las planillas de Adherentes con firmas (los avales). Deberán presentarse presencialmente en la Secretaria Electoral o mediante Correo electronico a registro@electoralchubut.gob.ar.',
    ref: 'Art. 19',
    sinCarga: true,
    plantilla: { url: '/reconocimiento/planilla-adherentes-firmas', filename: 'Planilla de Adherentes con firmas.pdf' },
  },
];

// Filas que SÍ exigen archivo: es el gate real de "documentación completa".
const DOCS_A_CARGAR = DOCS_OBLIGATORIOS.filter((d) => !d.sinCarga);

// Los CERTIFICADORES van al final: no son un órgano de conducción, son la sección de
// quienes avalan las fichas y los avales. A diferencia de los otros tres —que acá son
// solo "sugeridos"— esta sección SÍ es obligatoria: sin certificador no hay quien
// avale las planillas de adherentes del Art. 19 (ver utils/certificadoresFirmas.ts).
const ORGANOS_OBLIGATORIOS = [
  { tipo: 'CONGRESO_CONVENCION', label: 'Congreso o Convención' },
  { tipo: 'COMITE_JUNTA_CONSEJO', label: 'Comité, Junta o Consejo' },
  { tipo: 'JUNTA_ELECTORAL', label: 'Junta Electoral' },
  { tipo: TIPO_ORGANO_CERTIFICADORES, label: LABEL_CERTIFICADORES },
];
const ORGANOS_OPCIONALES = [
  { tipo: 'MESA_DIRECTIVA', label: 'Mesa Directiva' },
  { tipo: 'TRIBUNAL_CONDUCTA', label: 'Tribunal de Conducta/Disciplina' },
  { tipo: 'JUNTA_DEPARTAMENTAL', label: 'Junta Departamental/Seccional' },
  { tipo: 'OTRO', label: 'Otro (especificar)' },
];
const orgLabel = (t: string) =>
  [...ORGANOS_OBLIGATORIOS, ...ORGANOS_OPCIONALES].find((o) => o.tipo === t)?.label || t;

// Cargos posibles para una autoridad promotora. "PROMOTORA" (valor especial)
// significa autoridad promotora sin cargo definido → en el backend cargo = null.
const CARGO_SIN_DEFINIR = 'PROMOTORA';
const CARGOS_PROMOTOR = ['PRESIDENTE', 'VICEPRESIDENTE', 'SECRETARIO', 'PROSECRETARIO', 'TESORERO', 'PROTESORERO', 'VOCAL'];

// Género completo (para mostrar el valor que viene del padrón) y el subconjunto
// que se ofrece cuando la persona NO está en el padrón y se carga a mano.
const GENEROS = [
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'NO_BINARIO', label: 'No binario' },
  { value: 'OTRO', label: 'Otro' },
  { value: 'NO_ESPECIFICA', label: 'Prefiero no especificar' },
];
const GENEROS_MANUAL = [
  { value: 'FEMENINO', label: 'Femenino' },
  { value: 'MASCULINO', label: 'Masculino' },
  { value: 'OTRO', label: 'Otro' },
];
const generoLabel = (v?: string) => GENEROS.find((g) => g.value === v)?.label || '—';

const soloLetras = (v: string) => v.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, '');

const pasos = ['Documentación', 'Órganos y autoridades', 'Apoderados', 'Confirmar'];

interface PersonaDni {
  key: string;
  dni: string;
  nombre: string;
  apellido: string;
  genero: string;
  estado: 'idle' | 'loading' | 'ok' | 'error';
  mensaje?: string;
  // DNI (frente/dorso): reutilizado de un documento verificado o recién subido.
  dniExisten?: boolean;
  dniFrentePath?: string;
  dniDorsoPath?: string;
  dniFrenteFile?: File | null;
  dniDorsoFile?: File | null;
  enPadron?: boolean; // true → datos precargados del padrón (solo lectura)
}
interface Promotor extends PersonaDni {
  cargo: string; // valor del desplegable (CARGO_SIN_DEFINIR o un cargo específico)
}
interface OrganoRecon {
  id: string;
  tipoOrgano: string;
  denominacionPersonalizada: string;
  esObligatorio: boolean;
  promotores: Promotor[];
}

let _seq = 0;
const nuevoId = () => `${Date.now()}-${_seq++}`;
const nuevaPersona = (): PersonaDni => ({ key: nuevoId(), dni: '', nombre: '', apellido: '', genero: '', estado: 'idle' });
// cargo arranca vacío: es obligatorio elegir (incluida la opción "sin cargo").
const nuevoPromotor = (): Promotor => ({ ...nuevaPersona(), cargo: '' });

// Valida un DNI contra el padrón (endpoint AUTENTICADO) y detecta si ya tiene un
// DNI verificado cargado. Requiere sesión — no expone datos sin login.
async function resolverDni(limpio: string): Promise<Partial<PersonaDni> & { ok: boolean }> {
  const r = await api.get(`/padrones/validar-dni-privado/${limpio}`);
  const data = r.data?.data;
  if (!data?.valido) {
    return { ok: false, estado: 'error', mensaje: data?.mensaje || 'DNI no encontrado en el padrón' };
  }
  let dniExisten = false;
  let dniFrentePath: string | undefined;
  let dniDorsoPath: string | undefined;
  try {
    const c = await documentoService.checkDniPersona(limpio);
    const d = c.data?.data;
    if (d?.frente && d?.dorso) {
      dniExisten = true;
      dniFrentePath = d.frente.path;
      dniDorsoPath = d.dorso.path;
    }
  } catch { /* sin documento previo: se pedirá subir */ }
  return {
    ok: true,
    estado: 'ok',
    mensaje: undefined,
    nombre: data.datos?.nombre ? soloLetras(data.datos.nombre) : '',
    apellido: data.datos?.apellido ? soloLetras(data.datos.apellido) : '',
    genero: data.datos?.genero || 'OTRO',
    dniExisten,
    dniFrentePath,
    dniDorsoPath,
  };
}

const personaDniIncompleta = (p: PersonaDni) =>
  !p.dni || !p.nombre || !p.apellido || p.estado !== 'ok' || (!p.dniExisten && (!p.dniFrenteFile || !p.dniDorsoFile));

export default function ReconocimientoDocumentacionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const recon = useReconocimientoApoderado();

  const [pasoActual, setPasoActual] = useState(0);
  const [docs, setDocs] = useState<Record<string, File | null>>({});
  const [organos, setOrganos] = useState<OrganoRecon[]>(
    ORGANOS_OBLIGATORIOS.map((o) => ({
      id: nuevoId(),
      tipoOrgano: o.tipo,
      // La sección de certificadores ya viene con su nombre: no se le pide al partido
      // que la denomine (no es un órgano del acta ni de la carta orgánica).
      denominacionPersonalizada: esSeccionCertificadores(o.tipo) ? LABEL_CERTIFICADORES : '',
      esObligatorio: true,
      promotores: [],
    })),
  );
  const [apoderados, setApoderados] = useState<PersonaDni[]>([]);
  const [loading, setLoading] = useState(false);
  const [subiendo, setSubiendo] = useState<{ current: number; total: number } | null>(null);

  // Guarda: si el trámite no está en la etapa correcta, no hay wizard que mostrar.
  useEffect(() => {
    if (!recon.isLoading && !recon.mostrarWizard) {
      navigate('/dashboard', { replace: true });
    }
  }, [recon.isLoading, recon.mostrarWizard, navigate]);

  // ---- Documentos ----
  const setDoc = (tipo: string, file: File | null) => {
    // El Listado de Adherentes solo admite Excel/ODS.
    if (file && TIPOS_SOLO_EXCEL.includes(tipo) && !esArchivoExcelOds(file.name)) {
      toast.error(msgSoloExcel(tipo));
      return;
    }
    setDocs((p) => ({ ...p, [tipo]: file }));
  };
  const docsCompletos = DOCS_A_CARGAR.every((d) => !!docs[d.tipo]);

  // Las plantillas se sirven desde el backend (misma fuente que los adjuntos del
  // correo) y esos endpoints piden token: por eso se bajan como blob en lugar de un
  // <a href download>, que iría sin la cabecera de autorización.
  const descargarPlantilla = async (endpoint: string, filename: string) => {
    try {
      const res = await api.get(endpoint, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      toast.error(`No se pudo descargar ${filename}`);
    }
  };

  // ---- Órganos y promotores ----
  const agregarOrgano = (tipo: string) =>
    setOrganos((p) => [
      ...p,
      { id: nuevoId(), tipoOrgano: tipo, denominacionPersonalizada: '', esObligatorio: false, promotores: [] },
    ]);
  const eliminarOrgano = (id: string) => setOrganos((p) => p.filter((o) => o.id !== id));
  const updateOrgano = (id: string, campo: keyof OrganoRecon, valor: string) =>
    setOrganos((p) => p.map((o) => (o.id === id ? { ...o, [campo]: valor } : o)));
  // En la sección de certificadores hay un solo cargo posible, así que la persona
  // nueva ya viene con el cargo puesto, y el tope de 3 se aplica al agregar.
  const agregarPromotor = (organoId: string) =>
    setOrganos((p) =>
      p.map((o) => {
        if (o.id !== organoId) return o;
        if (esSeccionCertificadores(o.tipoOrgano) && o.promotores.length >= MAX_CERTIFICADORES) {
          toast.error(`Podés cargar hasta ${MAX_CERTIFICADORES} certificadores.`);
          return o;
        }
        const nuevo = esSeccionCertificadores(o.tipoOrgano)
          ? { ...nuevoPromotor(), cargo: CARGO_CERTIFICADOR_FIRMAS }
          : nuevoPromotor();
        return { ...o, promotores: [...o.promotores, nuevo] };
      }),
    );
  const eliminarPromotor = (organoId: string, key: string) =>
    setOrganos((p) => p.map((o) => (o.id === organoId ? { ...o, promotores: o.promotores.filter((x) => x.key !== key) } : o)));
  const updatePromotor = (organoId: string, key: string, patch: Partial<Promotor>) =>
    setOrganos((p) =>
      p.map((o) => (o.id === organoId ? { ...o, promotores: o.promotores.map((x) => (x.key === key ? { ...x, ...patch } : x)) } : o)),
    );
  const validarDniPromotor = async (organoId: string, key: string, dni: string) => {
    const limpio = dni.replace(/\D/g, '');
    if (limpio.length < 7) { updatePromotor(organoId, key, { estado: 'idle', mensaje: undefined }); return; }
    updatePromotor(organoId, key, { estado: 'loading' });
    try { updatePromotor(organoId, key, await resolverDni(limpio) as Partial<Promotor>); }
    catch { updatePromotor(organoId, key, { estado: 'error', mensaje: 'No se pudo validar el DNI' }); }
  };

  // ---- Apoderados promotores ----
  const agregarApoderado = () => setApoderados((p) => [...p, nuevaPersona()]);
  const eliminarApoderado = (key: string) => setApoderados((p) => p.filter((a) => a.key !== key));
  const updateApoderado = (key: string, patch: Partial<PersonaDni>) =>
    setApoderados((p) => p.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  // Apoderados: NO requieren estar en el padrón (Ley XII-23 Art. 17/18; Cód.
  // Electoral Art. 62). Si ESTÁN en el padrón, se precargan sus datos (solo
  // lectura); si no, se cargan a mano (género limitado). Nunca bloquea por padrón.
  const chequearDniApoderado = async (key: string, dni: string) => {
    const limpio = dni.replace(/\D/g, '');
    if (limpio.length < 7) { updateApoderado(key, { estado: 'idle', mensaje: undefined, enPadron: undefined, dniExisten: undefined }); return; }
    updateApoderado(key, { estado: 'loading' });
    const patch: Partial<PersonaDni> = { estado: 'ok', mensaje: undefined, enPadron: false, dniExisten: false };
    try {
      const r = await api.get(`/padrones/validar-dni-privado/${limpio}`);
      const d = r.data?.data;
      if (d?.valido) {
        patch.enPadron = true;
        patch.nombre = d.datos?.nombre ? soloLetras(d.datos.nombre) : '';
        patch.apellido = d.datos?.apellido ? soloLetras(d.datos.apellido) : '';
        patch.genero = d.datos?.genero || '';
      }
    } catch { /* padrón no disponible: se sigue con carga manual */ }
    try {
      const c = await documentoService.checkDniPersona(limpio);
      const dd = c.data?.data;
      if (dd?.frente && dd?.dorso) { patch.dniExisten = true; patch.dniFrentePath = dd.frente.path; patch.dniDorsoPath = dd.dorso.path; }
    } catch { /* sin documento previo */ }
    updateApoderado(key, patch);
  };

  // Un promotor es inválido si le faltan datos/DNI o si no eligió cargo.
  const promotoresInvalidos = organos.flatMap((o) => o.promotores.filter((p) => personaDniIncompleta(p) || !p.cargo));
  // Apoderado: nombre/apellido/género a mano (sin padrón) + DNI (reuso o subir).
  const apoderadoIncompleto = (a: PersonaDni) =>
    a.dni.replace(/\D/g, '').length < 7 || !a.nombre.trim() || !a.apellido.trim() || !a.genero ||
    a.estado === 'error' || a.estado === 'loading' || (!a.dniExisten && (!a.dniFrenteFile || !a.dniDorsoFile));
  const apoderadosInvalidos = apoderados.filter(apoderadoIncompleto);
  // Órganos con autoridades cargadas pero sin nombre (denominación) → obligatorio.
  // La sección de certificadores no se denomina: su nombre es fijo.
  const organosSinNombre = organos.filter(
    (o) => !esSeccionCertificadores(o.tipoOrgano) && o.promotores.length > 0 && !o.denominacionPersonalizada.trim(),
  );
  // A diferencia de los órganos —que acá son solo sugeridos— la sección de
  // certificadores exige al menos una persona: es quien avala las planillas del Art. 19.
  const sinCertificadores = !organos.some(
    (o) => esSeccionCertificadores(o.tipoOrgano) && o.promotores.length > 0,
  );
  const certificadoresDeMas = organos.some(
    (o) => esSeccionCertificadores(o.tipoOrgano) && o.promotores.length > MAX_CERTIFICADORES,
  );
  const msgCertificadores = sinCertificadores
    ? `Cargá al menos un ${LABEL_CARGO_CERTIFICADOR}: es quien avala las fichas y las planillas de adherentes.`
    : certificadoresDeMas
      ? `Podés cargar hasta ${MAX_CERTIFICADORES} certificadores de firmas.`
      : null;
  const totalPromotores = organos.reduce((n, o) => n + o.promotores.length, 0);

  const siguiente = () => {
    // El conteo sale de la config, no hardcodeado: si cambia la lista, el mensaje
    // acompaña solo (los avales no cuentan, no se suben).
    if (pasoActual === 0 && !docsCompletos) { toast.error(`Cargá los ${DOCS_A_CARGAR.length} documentos obligatorios para continuar.`); return; }
    if (pasoActual === 1) {
      if (msgCertificadores) { toast.error(msgCertificadores); return; }
      if (organosSinNombre.length > 0) { toast.error('Ponele un nombre a cada órgano con autoridades cargadas.'); return; }
      if (promotoresInvalidos.length > 0) { toast.error('Revisá las autoridades: cargo, DNI válido y foto del DNI (frente y dorso).'); return; }
    }
    if (pasoActual === 2 && apoderadosInvalidos.length > 0) { toast.error('Revisá los apoderados: DNI válido y foto del DNI (frente y dorso).'); return; }
    setPasoActual((p) => Math.min(p + 1, pasos.length - 1));
  };
  const anterior = () => setPasoActual((p) => Math.max(p - 1, 0));

  // Bloque reutilizable: reutilizar DNI verificado o subir frente/dorso.
  const renderDniDocs = (persona: PersonaDni, onFile: (campo: 'dniFrenteFile' | 'dniDorsoFile', file: File | null) => void) => {
    if (persona.estado !== 'ok') return null;
    return (
      <div className="mt-3 pt-3 border-t">
        {persona.dniExisten ? (
          <p className="text-xs text-green-700">✓ DNI ya cargado y verificado en el sistema — se reutiliza automáticamente.</p>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {(['dniFrenteFile', 'dniDorsoFile'] as const).map((campo, idx) => {
              const file = persona[campo];
              return (
                <div key={campo}>
                  <label className="block text-xs text-gray-500 mb-1">DNI {idx === 0 ? 'Frente' : 'Dorso'} *</label>
                  <div className="flex items-center gap-2">
                    {file && <span className="text-xs text-green-700 truncate max-w-[110px]">✓ {file.name}</span>}
                    <label className="cursor-pointer text-xs font-medium text-green-700 hover:text-green-800">
                      {file ? 'Cambiar' : 'Adjuntar'}
                      <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => onFile(campo, e.target.files?.[0] || null)} />
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const enviar = async () => {
    if (!recon.expedienteId) return;
    if (!docsCompletos) { toast.error('Faltan documentos obligatorios.'); setPasoActual(0); return; }
    if (msgCertificadores) { toast.error(msgCertificadores); setPasoActual(1); return; }
    if (organosSinNombre.length > 0) { toast.error('Ponele un nombre a cada órgano con autoridades cargadas.'); setPasoActual(1); return; }
    if (promotoresInvalidos.length > 0) { toast.error('Hay autoridades sin cargo o con datos/DNI incompletos.'); setPasoActual(1); return; }
    if (apoderadosInvalidos.length > 0) { toast.error('Hay apoderados con datos incompletos o DNI inválido.'); setPasoActual(2); return; }
    setLoading(true);
    try {
      // 1) Reunir TODOS los archivos nuevos (docs Art.18 + DNI de promotores y
      //    apoderados que no reutilizan uno verificado) y subirlos en un lote.
      const artArchivos = DOCS_OBLIGATORIOS
        .map((d) => ({ key: `doc:${d.tipo}`, tipo: d.tipo as string, file: docs[d.tipo] }))
        .filter((a): a is { key: string; tipo: string; file: File } => !!a.file);

      const dniArchivos: Array<{ key: string; file: File }> = [];
      const pushDni = (prefijo: string, p: PersonaDni) => {
        if (p.dniExisten) return;
        if (p.dniFrenteFile) dniArchivos.push({ key: `${prefijo}f:${p.key}`, file: p.dniFrenteFile });
        if (p.dniDorsoFile) dniArchivos.push({ key: `${prefijo}d:${p.key}`, file: p.dniDorsoFile });
      };
      organos.forEach((o) => o.promotores.forEach((p) => pushDni('dni', p)));
      apoderados.forEach((a) => pushDni('apod', a));

      const todos = [...artArchivos.map((a) => ({ key: a.key, file: a.file })), ...dniArchivos];
      setSubiendo({ current: 0, total: todos.length });
      toast.loading('Subiendo documentos...', { id: 'upload-recon' });

      const filesMetadata = todos.map((a) => ({ fileName: a.file.name, contentType: a.file.type }));
      const urlsResp = await api.post('/storage/upload-urls', { files: filesMetadata });
      const urls: { url: string; path: string }[] = urlsResp.data.data;
      if (!urls || urls.length !== todos.length) throw new Error('No se recibieron las URLs de subida correctamente');

      let completados = 0;
      const pathPorKey: Record<string, string> = {};
      await Promise.all(
        todos.map(async (a, i) => {
          await fetch(urls[i].url, { method: 'PUT', body: a.file, headers: { 'Content-Type': a.file.type } });
          pathPorKey[a.key] = urls[i].path;
          completados++;
          setSubiendo({ current: completados, total: todos.length });
        }),
      );
      toast.dismiss('upload-recon');

      const documentos = artArchivos.map((a) => ({
        tipo: a.tipo, path: pathPorKey[a.key], nombreOriginal: a.file.name, mimeType: a.file.type, tamanio: a.file.size,
      }));

      const organosPayload = organos
        .filter((o) => o.promotores.length > 0)
        .map((o) => ({
          tipoOrgano: o.tipoOrgano,
          denominacion: o.denominacionPersonalizada || orgLabel(o.tipoOrgano),
          denominacionPersonalizada: o.denominacionPersonalizada || null,
          esObligatorio: o.esObligatorio,
          promotores: o.promotores.map((p) => ({
            dni: p.dni.replace(/\D/g, ''),
            nombre: p.nombre.trim(),
            apellido: p.apellido.trim(),
            genero: p.genero || 'OTRO',
            cargo: p.cargo === CARGO_SIN_DEFINIR ? null : p.cargo,
            dniFrentePath: p.dniExisten ? p.dniFrentePath : pathPorKey[`dnif:${p.key}`],
            dniDorsoPath: p.dniExisten ? p.dniDorsoPath : pathPorKey[`dnid:${p.key}`],
          })),
        }));

      const apoderadosPayload = apoderados.map((a) => ({
        dni: a.dni.replace(/\D/g, ''),
        nombre: a.nombre.trim(),
        apellido: a.apellido.trim(),
        genero: a.genero || 'OTRO',
        dniFrentePath: a.dniExisten ? a.dniFrentePath : pathPorKey[`apodf:${a.key}`],
        dniDorsoPath: a.dniExisten ? a.dniDorsoPath : pathPorKey[`apodd:${a.key}`],
      }));

      await reconocimientoService.cargarDocumentacion(recon.expedienteId, {
        organos: organosPayload,
        apoderados: apoderadosPayload,
        documentos,
      });

      await queryClient.invalidateQueries({ queryKey: ['mi-expediente'] });
      toast.success('Documentación, autoridades y apoderados promotores enviados');
      navigate('/dashboard', { replace: true });
    } catch (e: any) {
      toast.dismiss('upload-recon');
      toast.error(e?.response?.data?.error?.message || e?.message || 'Error al enviar la documentación', { duration: 8000 });
    } finally {
      setLoading(false);
      setSubiendo(null);
    }
  };

  if (recon.isLoading || !recon.mostrarWizard) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50">
        <div className="text-gray-500">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-teal-50 to-cyan-50">
      <header className="bg-white shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">🏛️</div>
            <div>
              <h1 className="font-bold text-gray-900">Documentación del partido</h1>
              <p className="text-sm text-gray-500">Constitución — {recon.miExpediente?.numero}</p>
            </div>
          </div>
          <button onClick={() => navigate('/dashboard')} className="text-gray-500 hover:text-gray-700 text-sm">← Volver al inicio</button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Stepper */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {pasos.map((titulo, i) => (
            <div key={titulo} className="flex items-center">
              <div
                onClick={() => { if (i <= pasoActual) setPasoActual(i); }}
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-medium cursor-pointer ${
                  pasoActual > i ? 'bg-green-600 border-green-600 text-white' : pasoActual === i ? 'bg-green-100 border-green-600 text-green-600' : 'border-gray-300 text-gray-400'
                }`}
              >
                {pasoActual > i ? '✓' : i + 1}
              </div>
              <span className={`ml-2 text-xs hidden md:inline ${pasoActual >= i ? 'text-green-600' : 'text-gray-400'}`}>{titulo}</span>
              {i < pasos.length - 1 && <div className={`w-8 md:w-16 h-0.5 mx-2 ${pasoActual > i ? 'bg-green-600' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          {/* Paso 0: Documentos */}
          {pasoActual === 0 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Documentación obligatoria (Art. 18/19)</h2>
              <p className="text-gray-600 mb-6">
                Adjuntá cada documento (PDF o imagen; el <strong>Listado de Adherentes</strong> en Excel).
                Descargá las plantillas donde estén disponibles. Todos son obligatorios; los avales
                se entregan en papel y por eso esa fila no pide archivo.
              </p>
              <div className="space-y-3">
                {DOCS_OBLIGATORIOS.map((d) => (
                  <div
                    key={d.tipo}
                    className={`border rounded-lg p-4 ${
                      d.sinCarga ? 'border-blue-200 bg-blue-50' : docs[d.tipo] ? 'border-green-300 bg-green-50' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="font-medium text-gray-800">
                          {d.label}
                          {d.ayuda && <span className="ml-2 text-xs font-normal text-gray-400">({d.ayuda})</span>}
                        </p>
                        <p className="text-xs text-gray-500">{d.ref}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {!d.sinCarga && docs[d.tipo] && (
                          <span className="text-sm text-green-700 truncate max-w-[160px]">✓ {docs[d.tipo]!.name}</span>
                        )}
                        {d.plantilla && (
                          <button
                            type="button"
                            onClick={() => descargarPlantilla(d.plantilla!.url, d.plantilla!.filename)}
                            className="text-sm font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap"
                          >
                            ⬇ Descargar plantilla
                          </button>
                        )}
                        {/* Donde iría "Adjuntar" va la aclaración: esta pieza no se sube. */}
                        {d.sinCarga ? (
                          <span className="text-sm font-medium text-blue-700 whitespace-nowrap">
                            No se adjunta acá
                          </span>
                        ) : (
                          <label className="cursor-pointer text-sm font-medium text-green-700 hover:text-green-800">
                            {docs[d.tipo] ? 'Cambiar' : 'Adjuntar'}
                            <input type="file" accept={acceptParaTipo(d.tipo)} className="hidden" onChange={(e) => setDoc(d.tipo, e.target.files?.[0] || null)} />
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Paso 1: Órganos y autoridades promotoras */}
          {pasoActual === 1 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Órganos y autoridades promotoras</h2>
              <p className="text-gray-600 mb-4">
                Declará los órganos previstos en el acta constitutiva y cargá sus autoridades promotoras.
                No es obligatorio completar todos los cargos ni todos los órganos.
              </p>
              <p className="text-gray-600 mb-4">
                Al final hay una sección aparte para los <strong>{LABEL_CERTIFICADORES}</strong>: no es un
                órgano, son las personas que avalan las fichas de afiliación y las planillas de adherentes
                de todo el partido. Esa sección <strong>sí es obligatoria</strong>: cargá entre 1 y {MAX_CERTIFICADORES}.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-sm text-blue-800">
                ℹ️ Cada autoridad queda registrada como <strong>promotora (fundacional)</strong>. Elegí un cargo si el acta lo define, o dejá <strong>"Autoridad promotora (sin cargo)"</strong>. Todas se validan contra el padrón.
              </div>

              <div className="space-y-6">
                {organos.map((organo) => {
                  const voc = vocabularioOrgano(organo.tipoOrgano);
                  const esCert = esSeccionCertificadores(organo.tipoOrgano);
                  return (
                  <div key={organo.id} className={`border-2 rounded-xl overflow-hidden ${esCert ? 'border-purple-200' : organo.esObligatorio ? 'border-green-200' : 'border-indigo-200'}`}>
                    <div className={`px-4 py-3 flex items-center justify-between ${esCert ? 'bg-purple-50' : organo.esObligatorio ? 'bg-green-50' : 'bg-indigo-50'}`}>
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{voc.icono}</span>
                        <div>
                          <h3 className="font-bold text-gray-900">{orgLabel(organo.tipoOrgano)}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${esCert ? 'bg-purple-100 text-purple-800' : organo.esObligatorio ? 'bg-green-100 text-green-800' : 'bg-indigo-100 text-indigo-800'}`}>
                            {esCert ? 'Obligatorio' : organo.esObligatorio ? 'Sugerido' : 'Opcional'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{voc.contarPersonas(organo.promotores.length)}</span>
                        {!organo.esObligatorio && (
                          <button type="button" onClick={() => eliminarOrgano(organo.id)} className="ml-2 w-6 h-6 bg-red-100 text-red-600 rounded-full hover:bg-red-200 text-xs">🗑️</button>
                        )}
                      </div>
                    </div>
                    <div className="p-4">
                      {esCert ? (
                        <p className={`text-sm rounded-lg p-3 mb-4 border ${organo.promotores.length === 0 ? 'text-red-800 bg-red-50 border-red-200' : 'text-purple-800 bg-purple-50 border-purple-100'}`}>
                          Quienes avalan las fichas de afiliación y las planillas de adherentes.
                          No es un órgano: la función alcanza a todo el partido, así que no lleva nombre.
                          Entre 1 y {MAX_CERTIFICADORES} personas, todas titulares.
                          {organo.promotores.length === 0 && <strong> Cargá al menos una.</strong>}
                        </p>
                      ) : (
                      <div className="mb-4">
                        <label className="block text-xs font-medium text-gray-600 mb-1">{voc.labelNombre}</label>
                        <input
                          type="text"
                          value={organo.denominacionPersonalizada}
                          onChange={(e) => updateOrgano(organo.id, 'denominacionPersonalizada', e.target.value)}
                          placeholder={orgLabel(organo.tipoOrgano)}
                          className={`w-full px-3 py-2 border rounded-lg text-sm ${organo.promotores.length > 0 && !organo.denominacionPersonalizada.trim() ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                        />
                        {organo.promotores.length > 0 && !organo.denominacionPersonalizada.trim() && (
                          <p className="text-xs text-red-600 mt-1">Ponele un nombre a este órgano.</p>
                        )}
                      </div>
                      )}

                      <div className="space-y-3">
                        {organo.promotores.map((p) => (
                          <div key={p.key} className="bg-gray-50 rounded-lg p-3 relative border">
                            <button type="button" onClick={() => eliminarPromotor(organo.id, p.key)} className="absolute top-2 right-2 w-6 h-6 bg-red-100 text-red-600 rounded-full hover:bg-red-200 text-xs">✕</button>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pr-6">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">DNI *</label>
                                <input
                                  type="text" value={p.dni} maxLength={8}
                                  onChange={(e) => updatePromotor(organo.id, p.key, { dni: e.target.value.replace(/\D/g, ''), estado: 'idle', nombre: '', apellido: '', dniExisten: undefined, dniFrentePath: undefined, dniDorsoPath: undefined })}
                                  onBlur={(e) => validarDniPromotor(organo.id, p.key, e.target.value)}
                                  className={`w-full px-2 py-1.5 border rounded text-sm ${p.estado === 'error' ? 'border-red-500 bg-red-50' : ''}`}
                                />
                                {p.estado === 'loading' && <span className="text-xs text-blue-600">Validando...</span>}
                                {p.estado === 'ok' && <span className="text-xs text-green-600">✓ DNI válido</span>}
                                {p.estado === 'error' && <span className="text-xs text-red-600">✗ {p.mensaje}</span>}
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Nombre</label>
                                <input type="text" value={p.nombre} readOnly disabled placeholder="Se completa con el DNI" className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700" />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Apellido</label>
                                <input type="text" value={p.apellido} readOnly disabled placeholder="Se completa con el DNI" className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700" />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Cargo *</label>
                                {/* En la sección de certificadores el cargo es único: se
                                    muestra fijo en vez de un desplegable de una opción. */}
                                {esCert ? (
                                  <input
                                    type="text" value={LABEL_CARGO_CERTIFICADOR} readOnly disabled
                                    className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700"
                                  />
                                ) : (
                                  <select value={p.cargo} onChange={(e) => updatePromotor(organo.id, p.key, { cargo: e.target.value })} className={`w-full px-2 py-1.5 border rounded text-sm bg-white ${!p.cargo ? 'border-red-400 text-gray-500' : ''}`}>
                                    <option value="" disabled>Seleccioná un cargo…</option>
                                    <option value={CARGO_SIN_DEFINIR}>Autoridad promotora (sin cargo)</option>
                                    {CARGOS_PROMOTOR.map((c) => <option key={c} value={c}>{c.charAt(0) + c.slice(1).toLowerCase()}</option>)}
                                  </select>
                                )}
                              </div>
                            </div>
                            {renderDniDocs(p, (campo, file) => updatePromotor(organo.id, p.key, { [campo]: file } as Partial<Promotor>))}
                          </div>
                        ))}
                      </div>

                      {/* En certificadores el botón desaparece al llegar al tope. */}
                      {!(esCert && organo.promotores.length >= MAX_CERTIFICADORES) && (
                        <button type="button" onClick={() => agregarPromotor(organo.id)} className={`mt-3 text-sm font-medium ${esCert ? 'text-purple-700 hover:text-purple-800' : 'text-green-700 hover:text-green-800'}`}>
                          ＋ {esCert ? voc.labelAgregarPersona : 'Agregar autoridad promotora'}
                        </button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>

              <div className="mt-6 border-t pt-4">
                <p className="text-xs font-medium text-gray-500 mb-2">Agregar otro órgano</p>
                <div className="flex flex-wrap gap-2">
                  {ORGANOS_OPCIONALES.map((o) => (
                    <button key={o.tipo} type="button" onClick={() => agregarOrgano(o.tipo)} className="px-3 py-1.5 text-sm rounded-lg border border-indigo-300 text-indigo-700 hover:bg-indigo-50">
                      ＋ {o.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Paso 2: Apoderados promotores */}
          {pasoActual === 2 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Apoderados promotores</h2>
              <p className="text-gray-600 mb-4">Cargá los apoderados designados en el acta constitutiva. Podés dejar esta sección vacía si no corresponde.</p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 text-sm text-blue-800">
                ℹ️ Si el iniciador del trámite también es apoderado, cargalo aquí.
              </div>

              <div className="space-y-3">
                {apoderados.map((a) => (
                  <div key={a.key} className="bg-gray-50 rounded-lg p-3 relative border">
                    <button type="button" onClick={() => eliminarApoderado(a.key)} className="absolute top-2 right-2 w-6 h-6 bg-red-100 text-red-600 rounded-full hover:bg-red-200 text-xs">✕</button>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pr-6">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">DNI *</label>
                        <input
                          type="text" value={a.dni} maxLength={8}
                          onChange={(e) => updateApoderado(a.key, { dni: e.target.value.replace(/\D/g, ''), estado: 'idle', enPadron: undefined, nombre: '', apellido: '', genero: '', dniExisten: undefined, dniFrentePath: undefined, dniDorsoPath: undefined })}
                          onBlur={(e) => chequearDniApoderado(a.key, e.target.value)}
                          className={`w-full px-2 py-1.5 border rounded text-sm ${a.estado === 'error' ? 'border-red-500 bg-red-50' : ''}`}
                        />
                        {a.estado === 'loading' && <span className="text-xs text-blue-600">Verificando...</span>}
                        {a.estado === 'ok' && a.enPadron && <span className="text-xs text-green-600">✓ En el padrón</span>}
                        {a.estado === 'ok' && !a.enPadron && <span className="text-xs text-amber-600">No está en el padrón — cargá los datos</span>}
                        {a.estado === 'error' && <span className="text-xs text-red-600">✗ {a.mensaje}</span>}
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Nombre *</label>
                        <input type="text" value={a.nombre} readOnly={a.enPadron} disabled={a.enPadron} onChange={(e) => updateApoderado(a.key, { nombre: soloLetras(e.target.value) })} className={`w-full px-2 py-1.5 border rounded text-sm ${a.enPadron ? 'bg-gray-100 text-gray-700' : ''}`} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Apellido *</label>
                        <input type="text" value={a.apellido} readOnly={a.enPadron} disabled={a.enPadron} onChange={(e) => updateApoderado(a.key, { apellido: soloLetras(e.target.value) })} className={`w-full px-2 py-1.5 border rounded text-sm ${a.enPadron ? 'bg-gray-100 text-gray-700' : ''}`} />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Género *</label>
                        {a.enPadron ? (
                          <input type="text" value={generoLabel(a.genero)} readOnly disabled className="w-full px-2 py-1.5 border rounded text-sm bg-gray-100 text-gray-700" />
                        ) : (
                          <select value={a.genero} onChange={(e) => updateApoderado(a.key, { genero: e.target.value })} className={`w-full px-2 py-1.5 border rounded text-sm bg-white ${!a.genero ? 'border-red-400 text-gray-500' : ''}`}>
                            <option value="" disabled>Elegí…</option>
                            {GENEROS_MANUAL.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                          </select>
                        )}
                      </div>
                    </div>
                    {a.estado !== 'error' && a.dni.replace(/\D/g, '').length >= 7 && renderDniDocs({ ...a, estado: 'ok' }, (campo, file) => updateApoderado(a.key, { [campo]: file }))}
                  </div>
                ))}
              </div>

              <button type="button" onClick={agregarApoderado} className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-500 hover:text-green-600">
                ＋ Agregar apoderado promotor
              </button>
            </div>
          )}

          {/* Paso 3: Confirmar */}
          {pasoActual === 3 && (
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Revisá y enviá</h2>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Documentos ({DOCS_A_CARGAR.filter((d) => docs[d.tipo]).length}/{DOCS_A_CARGAR.length})</h3>
                  <ul className="text-sm space-y-1">
                    {DOCS_A_CARGAR.map((d) => (
                      <li key={d.tipo} className={docs[d.tipo] ? 'text-green-700' : 'text-red-600'}>{docs[d.tipo] ? '✓' : '✗'} {d.label}</li>
                    ))}
                    {/* Los avales no se cuentan como carga: se entregan en papel. Se
                        listan igual, para que el partido no crea que se los olvidó. */}
                    {DOCS_OBLIGATORIOS.filter((d) => d.sinCarga).map((d) => (
                      <li key={d.tipo} className="text-blue-700">📄 {d.label}</li>
                    ))}
                  </ul>
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Órganos y autoridades promotoras ({totalPromotores})</h3>
                  {organos.filter((o) => o.promotores.length > 0).length === 0 ? (
                    <p className="text-sm text-amber-700">No cargaste autoridades promotoras. Podés volver atrás para agregarlas.</p>
                  ) : (
                    <ul className="text-sm space-y-2">
                      {organos.filter((o) => o.promotores.length > 0).map((o) => (
                        <li key={o.id}>
                          <span className="font-medium text-gray-700">{o.denominacionPersonalizada || orgLabel(o.tipoOrgano)}:</span>{' '}
                          <span className="text-gray-600">{o.promotores.map((p) => `${p.nombre} ${p.apellido}${p.cargo !== CARGO_SIN_DEFINIR ? ` (${p.cargo.toLowerCase()})` : ''}`).join(', ')}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="border rounded-lg p-4">
                  <h3 className="font-semibold text-gray-800 mb-2">Apoderados promotores ({apoderados.length})</h3>
                  {apoderados.length === 0 ? (
                    <p className="text-sm text-gray-500">Sin apoderados adicionales.</p>
                  ) : (
                    <ul className="text-sm text-gray-600 space-y-1">
                      {apoderados.map((a) => <li key={a.key}>{a.nombre} {a.apellido} — DNI {a.dni}</li>)}
                    </ul>
                  )}
                </div>
              </div>
              {subiendo && <p className="mt-4 text-sm text-blue-600">Subiendo documentos {subiendo.current}/{subiendo.total}...</p>}
            </div>
          )}

          {/* Navegación */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t">
            <button type="button" onClick={anterior} disabled={pasoActual === 0 || loading} className="px-5 py-2 rounded-lg border border-gray-300 text-gray-700 disabled:opacity-40">
              ← Anterior
            </button>
            {pasoActual < pasos.length - 1 ? (
              <button type="button" onClick={siguiente} className="px-6 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium">Siguiente →</button>
            ) : (
              <button type="button" onClick={enviar} disabled={loading} className="px-6 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-60">
                {loading ? 'Enviando...' : 'Enviar documentación'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
